import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { object } from 'prop-types'
import { updateSterilizationCalls, updateSelectedCalls } from '@store/slice/sterilizationSlice'
import { updateNotification } from '@store/slice/mainSlice'
import { get, post } from '@util/xhrAPI'
import { useTheme } from '@table-library/react-table-library/theme'
import { getTheme } from '@table-library/react-table-library/baseline'
import {
  Table,
  Header,
  HeaderRow,
  Body,
  Row,
  HeaderCell,
  Cell,
} from "@table-library/react-table-library/table"
import usePathGenerator from '@hook/usePathGenerator'
import useDeleteButton from '@hook/useDeleteButton'
import useToggle from "@hook/useToggle"
import useFormData from "@hook/useFormData"
import Buttons from '@ui/elements/Buttons'
import Div from '@ui/layout/Div'
import InputType from '@ui/inputs/InputType'

const SterilizationsListTable = ({ dataSterilizationCalls }) => {
  const [t] = useTranslation(['common', 'sterilization'])
  const { calls, selectedCalls, selectedSeason } = useSelector(state => state.sterilization)
  const { mission } = useSelector(state => state.main)
  const { isCompany, isCoOwnership } = mission
  const [isAllSelected, toggleAllSelected] = useToggle(false)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const deleteButton = useDeleteButton()
  const renderFormData = useFormData()
  const theme = useTheme([getTheme(), {
    Table: `
         --data-table-library_grid-template-columns: 10% 15% 22% 30% repeat(${isCompany || isCoOwnership ? 3 : 2}, minmax(0, 1fr));
      `,
  },])
  const resize = { resizerHighlight: "#dde2eb", resizerWidth: 5 }

  const getDynamicPath = usePathGenerator()

  const goToShowPage = id => navigate(getDynamicPath('sterilizationShow', { id }))

  const renderMissionSubjectsStat = (id, missionSubjectsStat) => {
    if (mission.missionSubjects.length > 1) {
      return mission.missionSubjects.map(missionSubject => {
        return (
          <h5 key={`${id}-${missionSubject.label}-stat`}>
            <span>{missionSubject.label}</span>: {missionSubjectsStat[missionSubject.label] || 0}
          </h5>
        )
      })
    }
  }

  const removeCall = (id, isPermanent = false) => {
    const sterilizationCall = calls[selectedSeason][id]
    const removeLink = isPermanent ? sterilizationCall.links.remove : `/api/missions/${mission.id}/mission-seasons/${selectedSeason}/calls/remove/${id}`
    get(`${import.meta.env.VITE_API_URL}${removeLink}`, response => {
      const { code } = response
      if (code == 200) {
        const callsList = { ...calls[selectedSeason] }
        delete callsList[id]
        dispatch(updateSterilizationCalls({ ...calls, [selectedSeason]: callsList }))
        dispatch(updateNotification({ type: 'is-success', message: t('messages.removed_successfuly', { title: sterilizationCall.name }) }))
      } else {
        dispatch(updateNotification({ type: 'is-danger', message: t('messages.error_occured') }))
      }
    })
  }

  const isChecked = id => {
    const list = selectedCalls?.split(',') || []
    return list.indexOf(id.toString()) >= 0
  }

  const updateSelectionList = event => {
    const id = event.target.value
    const list = selectedCalls?.split(',') || []
    if (event.target.checked) {
      list.push(id)
      dispatch(updateSelectedCalls(list.join(',')))
    } else {
      const updatedList = list.filter(item => item !== id)
      dispatch(updateSelectedCalls(updatedList.length ? updatedList.join(',') : null))
    }
  }

  const getImportButtonStyle = () => {
    const rect = document.querySelector('.sterilizations').getBoundingClientRect()
    return { 'right': `${rect.left}px` }
  }

  const importSelectedCallsOnTheActiveSeason = () => {
    const data = {
      sterilizationCalls: selectedCalls
    }
    post(`${import.meta.env.VITE_API_URL}/api/missions/${mission.id}/calls/associate-sterilization-calls`, renderFormData(data), response => {
      const { code, errors } = response
      if (200 === code) {
        dispatch(updateNotification({ type: 'is-success', message: t('sterilization:messages.call_created_successfuly') }))
        dispatch(updateSelectedCalls(null))
        toggleAllSelected()
      } else {
        console.log(errors)
      }
    })
  }
  const addressesSelectionToggle = () => {
    isAllSelected ? dispatch(updateSelectedCalls(null)) : dispatch(updateSelectedCalls(Object.keys(calls).join(',')))
    toggleAllSelected()
  }

  return (
    <>
      <Table data={dataSterilizationCalls} theme={theme} layout={{ custom: true }} className='sterilizations'>
        {(tableList) => (
          <>
            <Header>
              <HeaderRow>
                <HeaderCell resize={resize}>ID</HeaderCell>
                <HeaderCell resize={resize}>{t('table.FullName')}</HeaderCell>
                <HeaderCell resize={resize}>{t('table.Address')}</HeaderCell>
                <HeaderCell resize={resize}>{t('table.Treatment')}</HeaderCell>
                <HeaderCell resize={resize}>{t('table.Date')}</HeaderCell>
                {isCompany || isCoOwnership ? <HeaderCell resize={resize}>{t('table.CallerType')}</HeaderCell> : <></>}
                <HeaderCell resize={resize}>Actions</HeaderCell>
              </HeaderRow>
            </Header>
            <Body>
              {tableList.map(item =>
                <Row key={item.id} item={item} className={item.scansData.isSecondScanned ? 'is-second-scanned' : (item.scansData.isFirstScanned ? 'is-first-scanned' : 'non-scanned')}>
                  <Cell>
                    {mission.activeSeason.id !== selectedSeason && <InputType
                      cssClass='check-call'
                      value={item.id}
                      type='checkbox'
                      otherProps={{ onClick: updateSelectionList, checked: isChecked(item.id) }}
                    />}#{item.id}
                  </Cell>
                  <Cell>{item.addressResponsibleFullName}</Cell>
                  <Cell>{item.companyName ? `${item.companyName},` : ''}{item.sterilizationDistrict}, {item.sterilizationAddress},{item.sterilizationZipCode}</Cell>
                  <Cell className='scan-cell'>
                    <h4><span>{t('table.signaled_nests')}</span> :{item.nestsNumber}</h4>
                    <h3>{t('table.scan')}</h3>
                    <Div cssClass='columns'>
                      {item.scansData.isFirstScanned
                        ? <Div cssClass='column'>
                          <h4>{t('table.1st_scan')}</h4>
                          <h5><span>{t('table.found_nests')}</span> :{item.scansData.firstScan.totalNests}</h5>
                          <h5><span>{t('table.empty_nests')}</span> :{item.scansData.firstScan.totalEmptyNests}</h5>
                          <h5><span>{t('table.nb_eggs')}</span> :{item.scansData.firstScan.totalEggs}</h5>
                          <h5><span>{t('table.nb_non_treated_eggs')}</span> :{item.scansData.firstScan.totalNonTreatedEggs}</h5>
                          <h5><span>{t('table.nb_birds')}</span> :{item.scansData.firstScan.totalBirds}</h5>
                          {renderMissionSubjectsStat(item.id, item.scansData.firstScan.missionSubjects)}
                        </Div>
                        : <Div cssClass='column'>
                          <h4>{t('table.1st_scan')}</h4>
                          <h5>{t('table.pending')}</h5>
                        </Div>
                      }
                      {item.scansData.isSecondScanned
                        ? <Div cssClass='column'>
                          <h4>{t('table.2nd_scan')}</h4>
                          <h5><span>{t('table.found_nests')}</span> :{item.scansData.secondScan.totalNests}</h5>
                          <h5><span>{t('table.empty_nests')}</span> :{item.scansData.secondScan.totalEmptyNests}</h5>
                          <h5><span>{t('table.nb_eggs')}</span> :{item.scansData.secondScan.totalEggs}</h5>
                          <h5><span>{t('table.nb_non_treated_eggs')}</span> :{item.scansData.secondScan.totalNonTreatedEggs}</h5>
                          <h5><span>{t('table.nb_birds')}</span> :{item.scansData.secondScan.totalBirds}</h5>
                          {renderMissionSubjectsStat(item.id, item.scansData.secondScan.missionSubjects)}
                        </Div>
                        : <Div cssClass='column'>
                          <h4>{t('table.2nd_scan')}</h4>
                          <h5>{t('table.pending')}</h5>
                        </Div>
                      }
                    </Div>
                  </Cell>
                  <Cell>{item.createdAt}</Cell>
                  {
                    isCompany || isCoOwnership
                      ? <Cell className={!isCompany ? 'is-hidden' : ''}>{t(`table.${item.callType}`)}</Cell>
                      : <></>
                  }
                  <Cell className='table-actions'>
                    <Buttons buttons={[
                      {
                        icon: 'icon-clipboard',
                        cssClass: 'is-info is-small',
                        callback: () => goToShowPage(item.id)
                      }
                    ]} />
                    {deleteButton(item.id, item.sterilizationAddress, removeCall)}
                  </Cell>
                </Row>
              )}
            </Body>
          </>
        )}
      </Table>
      {
        selectedCalls
        && <Div cssClass='import-calls' style={getImportButtonStyle()}>
          <Buttons buttons={[
            {
              icon: isAllSelected ? 'icon-clear' : 'icon-done_all',
              cssClass: 'is-warning is-small',
              text: isAllSelected ? t('sterilization:actions.unselect_all') : t('sterilization:actions.select_all'),
              callback: () => addressesSelectionToggle()
            }, {
              icon: 'icon-recycle',
              cssClass: 'is-danger is-small',
              text: t('sterilization:actions.import_on_current_season'),
              callback: importSelectedCallsOnTheActiveSeason
            }
          ]} />
        </Div>}
    </>
  )
}

SterilizationsListTable.propTypes = {
  dataSterilizationCalls: object
}

export default SterilizationsListTable